/* Recipe Recalc recognition only.
 *
 * The result intentionally keeps a flat `items` array for the existing
 * calculator while adding optional structure that can be reviewed later.
 */

const UNIT_MAP = {
  "г": "g", "гр": "g", "грам": "g", "грамм": "g", "грамма": "g", "граммов": "g", "g": "g",
  "кг": "kg", "килограмм": "kg", "килограмма": "kg", "килограммов": "kg", "kg": "kg",
  "мл": "ml", "миллилитр": "ml", "миллилитра": "ml", "миллилитров": "ml", "ml": "ml",
  "л": "l", "литр": "l", "литра": "l", "литров": "l", "l": "l",
  "ст.л": "tbsp", "стл": "tbsp", "столовая": "tbsp", "tbsp": "tbsp",
  "ч.л": "tsp", "чл": "tsp", "чайная": "tsp", "tsp": "tsp",
  "стакан": "cup", "cup": "cup",
  "шт": "pcs", "штук": "pcs", "штуки": "pcs", "штука": "pcs", "pcs": "pcs",
  "щепотка": "pinch", "щепотки": "pinch", "по вкусу": "taste",
  "горсть": "handful", "горсти": "handful", "handful": "handful",
};

const FOOD_UNIT_SET = new Set([
  "g", "kg", "ml", "l", "tbsp", "tsp", "cup", "pcs", "pinch", "taste", "handful",
]);
const TIME_TEMP_UNIT_RE = /^(°?c|°|градус\w*|мин\w*|сек\w*|час\w*|ч\.?|м\.?|s|мс|days?|дн\w*|hour\w*|second\w*|minute\w*)$/i;
const TECH_VERB_RE = /^(взять|выпек\w*|выпеч\w*|печь|пропеч\w*|подпеч\w*|заморозить|замораж\w*|охлад\w*|остуд\w*|остыв\w*|убер\w*|убир\w*|остав\w*|поставить|постав\w*|перемеш\w*|взбить|взбива\w*|прогре\w*|дов[её]сти|довод\w*|варить|вар\w*|тушить|туш\w*|жарить|жар\w*|готов\w*|смеша\w*|соедин\w*|доба[вв]\w*|перелож\w*|снять|снима\w*|процед\w*|зали[тв]\w*|нагре\w*|разогр\w*|раздел\w*|раскат\w*|распредел\w*|накр\w*|измельч\w*|формов\w*|сформ\w*|темпер\w*|дать|давать|нанест\w*|нанос\w*|отсадить|отсаж\w*|стабилизи\w*|глазир\w*|карамелиз\w*|бланшир\w*|конден\w*)/i;

const AMOUNT_TOKEN_SOURCE = "(?:(?:\\d+\\s+)?\\d+\\/\\d+|\\d+(?:[.,]\\d+)?|[½¼])";
const AMOUNT_SOURCE = `(${AMOUNT_TOKEN_SOURCE})`;

const UNIT_PREFIXES = [
  ["столовых ложек", "ст.л"], ["столовые ложки", "ст.л"], ["столовая ложка", "ст.л"],
  ["чайных ложек", "ч.л"], ["чайные ложки", "ч.л"], ["чайная ложка", "ч.л"],
  ["миллилитров", "мл"], ["миллилитра", "мл"], ["миллилитр", "мл"],
  ["килограммов", "кг"], ["килограмма", "кг"], ["килограмм", "кг"],
  ["граммов", "гр"], ["грамма", "гр"], ["грамм", "гр"],
  ["стаканов", "стакан"], ["стакана", "стакан"], ["стакан", "стакан"],
  ["штуки", "шт"], ["штука", "шт"], ["штук", "шт"],
  ["литров", "л"], ["литра", "л"], ["литр", "л"],
  ["ст. л.", "ст.л"], ["ст.л.", "ст.л"], ["ст. л", "ст.л"], ["ст.л", "ст.л"],
  ["ч. л.", "ч.л"], ["ч.л.", "ч.л"], ["ч. л", "ч.л"], ["ч.л", "ч.л"],
  ["tbsp", "tbsp"], ["tsp", "tsp"], ["cup", "cup"],
  ["кг", "кг"], ["мл", "мл"], ["гр", "гр"], ["шт", "шт"],
  ["kg", "kg"], ["ml", "ml"], ["pcs", "pcs"],
  ["г", "г"], ["л", "л"], ["g", "g"], ["l", "l"],
].sort((a, b) => b[0].length - a[0].length);

const GENERIC_HEADINGS = new Set([
  "приготовление", "ингредиенты", "рецепт", "способ приготовления", "сборка",
]);
const METHOD_HEADINGS = new Set(["приготовление", "способ приготовления", "сборка"]);
const TECHNOLOGY_HEADINGS = new Map([
  ["тесто", "ТЕСТО"], ["бисквит", "БИСКВИТ"], ["ганаш", "ГАНАШ"],
  ["крем", "КРЕМ"], ["начинка", "НАЧИНКА"], ["сборка", "СБОРКА"],
  ["пропитка", "ПРОПИТКА"], ["декор", "ДЕКОР"], ["украшение", "УКРАШЕНИЕ"],
]);
const SECTION_NAMES = new Map([
  ["для бисквита", "Бисквит"], ["бисквит", "Бисквит"],
  ["для теста", "Тесто"], ["тесто", "Тесто"],
  ["для крема", "Крем"], ["крем", "Крем"],
  ["для начинки", "Начинка"], ["начинка", "Начинка"],
  ["для ганаша", "Ганаш"], ["ганаш", "Ганаш"],
  ["для украшения", "Декор"], ["украшение", "Декор"],
  ["для декора", "Декор"], ["декор", "Декор"],
  ["для пропитки", "Пропитка"], ["пропитка", "Пропитка"],
]);

const NAME_OVERRIDES = new Map([
  ["тыквенного пюре", "Тыквенное пюре"],
  ["сливочного масла", "Сливочное масло"],
  ["пшеничной муки", "Пшеничная мука"],
  ["сахара", "Сахар"], ["разрыхлителя", "Разрыхлитель"],
  ["соды", "Сода"], ["корицы", "Корица"], ["имбиря", "Имбирь"],
  ["кардамона", "Кардамон"], ["соли", "Соль"], ["изюма", "Изюм"],
  ["тёмного шоколада", "Тёмный шоколад"], ["темного шоколада", "Тёмный шоколад"],
  ["белого шоколада", "Белый шоколад"], ["молочного шоколада", "Молочный шоколад"],
  ["шоколада", "Шоколад"],
  ["коричневого сахара", "Коричневый сахар"],
  ["сливочного сыра", "Сливочный сыр"],
  ["миндальной муки", "Миндальная мука"],
  ["сливок 33–35%", "Сливки 33–35%"], ["сливок 33-35%", "Сливки 33–35%"],
  ["бадьяна или аниса", "Бадьян / анис"],
  ["обжаренных грецких орехов или пекана", "Грецкие орехи / пекан"],
  ["грецких орехов или пекана", "Грецкие орехи / пекан"],
  ["мелкого изюма", "Изюм"], ["изюма кишмиш", "Изюм кишмиш"],
  ["рубленые обжаренные грецкие орехи", "Грецкие орехи"],
  ["грецкие орехи", "Грецкие орехи"],
  ["муки", "Мука"], ["какао", "Какао"],
  ["растительного масла", "Растительное масло"],
  ["кипятка", "Кипяток"], ["воды", "Вода"],
  ["ванильного экстракта", "Ванильный экстракт"], ["экстракта ванили", "Ванильный экстракт"],
  ["лимонного сока", "Лимонный сок"], ["сока лимона", "Лимонный сок"],
  ["сливочно-творожного сыра", "Сливочно-творожный сыр"],
  ["сахарной пудры", "Сахарная пудра"],
  ["варёной сгущёнки", "Варёное сгущённое молоко"],
  ["вареной сгущенки", "Варёное сгущённое молоко"],
  ["обжаренного арахиса", "Арахис"], ["арахиса", "Арахис"],
]);

const parseAmount = (raw) => {
  const vulgarFractions = { "½": "1/2", "¼": "1/4" };
  const value = (vulgarFractions[String(raw || "").trim()] || String(raw || "").trim()).replace(",", ".");
  if (value.includes("/")) {
    const mixed = value.match(/^(?:(\d+)\s+)?(\d+)\/(\d+)$/);
    if (!mixed || Number(mixed[3]) === 0) return NaN;
    return Number(mixed[1] || 0) + Number(mixed[2]) / Number(mixed[3]);
  }
  return Number(value);
};

const headingKey = (line) => line.trim().replace(/:$/, "").trim().toLocaleLowerCase("ru");
const capitalize = (value) => value ? value.charAt(0).toLocaleUpperCase("ru") + value.slice(1) : "";
const stripListPrefix = (line) => {
  let cleaned = String(line || "").trim();
  // A numbered-list marker must be followed by whitespace. This preserves
  // decimal quantities such as 0.5 and 0.25 at the start of a line.
  cleaned = cleaned.replace(/^\s*(?:\d{1,3}[.)](?=\s)|\[[ xх]\])\s*/i, "");
  cleaned = cleaned.replace(
    /^\s*(?:(?:(?:\p{Extended_Pictographic}|\p{Emoji_Presentation})|[-–—•*·◆◇🔸🔹◈▪▫●○■□▶►➤➜→★☆✦✧❖❥♥♡])\uFE0F?)+\s*/u,
    ""
  );
  return cleaned.trim();
};

const INGREDIENT_STATE_RE = /^(?:растоплен[а-яё]*(?:\s+и\s+слегка\s+остыв[а-яё]*)?|холодн[а-яё]*|комнатн[а-яё]*\s+температур[а-яё]*)$/i;

const splitIngredientIdentity = (rawName) => {
  let value = stripListPrefix(rawName).trim();
  const notes = [];

  const [namePart, ...commaNotes] = value.split(",");
  value = namePart.trim();
  if (commaNotes.length) notes.push(commaNotes.join(",").trim());

  const dashState = value.match(/^(.+?)\s+[—–-]\s+(.+)$/);
  if (dashState) {
    value = dashState[1].trim();
    notes.push(dashState[2].trim());
  }

  // Conservative no-punctuation state extraction. Only the explicit,
  // unambiguous "melted" family is detached from the ingredient identity.
  const trailingMeltedState = value.match(/^(.+?)\s+(растоплен(?:ный|ная|ное|ного))$/i);
  if (trailingMeltedState) {
    value = trailingMeltedState[1].trim();
    notes.push(trailingMeltedState[2].trim());
  }

  if (/\s+без\s+запаха$/i.test(value)) {
    value = value.replace(/\s+без\s+запаха$/i, "").trim();
    notes.push("без запаха");
  }

  const leadingDescriptors = [
    [/^рублен[а-яё]*\s+(.+)$/i, "рубленые"],
    [/^обжаренн[а-яё]*\s+(.+)$/i, "обжаренные"],
    [/^измельч[её]нн[а-яё]*\s+(.+)$/i, "измельчённые"],
    [/^мелк[а-яё]*\s+(.+)$/i, "мелкий"],
    [/^просеянн[а-яё]*\s+(.+)$/i, "просеянная"],
    [/^мягк[а-яё]*\s+(.+)$/i, "мягкое"],
    [/^охлажд[её]нн[а-яё]*\s+(.+)$/i, "охлаждённое"],
  ];
  let descriptorFound = true;
  while (descriptorFound) {
    descriptorFound = false;
    for (const [pattern, note] of leadingDescriptors) {
      const descriptor = value.match(pattern);
      if (descriptor) {
        value = descriptor[1].trim();
        notes.push(note);
        descriptorFound = true;
        break;
      }
    }
  }

  const roomTemperature = value.match(/^(.+?)\s+комнатной\s+температуры$/i);
  if (roomTemperature) {
    value = roomTemperature[1].trim();
    notes.push("комнатной температуры");
  }

  const coldDescriptor = value.match(/^холодн(?:ое|ая|ый|ые|ого|ых)\s+(.+)$/i);
  if (coldDescriptor) {
    value = coldDescriptor[1].trim();
    notes.push(/^слив(?:ки|ок)(?=$|\s)/i.test(value) ? "холодные" : "холодное");
  }

  value = value.replace(/(слив(?:ки|ок)\s+33\s*[–-]\s*35%)\s+жирности$/i, "$1");
  return { name: value, note: notes.filter(Boolean).join(", ") || null };
};

const normalizeIngredientName = (rawName) => {
  const cleaned = String(rawName || "").trim().replace(/[.;:]$/, "");
  return NAME_OVERRIDES.get(cleaned.toLocaleLowerCase("ru")) || capitalize(cleaned);
};

const normalizeIngredientNote = (rawNote, ingredientName) => {
  if (!rawNote) return null;
  const note = rawNote.trim().replace(/[.;]$/, "");
  const key = note.toLocaleLowerCase("ru");
  if (/^растоплен[а-яё]*\s+и\s+слегка\s+остыв[а-яё]*$/.test(key)) return "растопить и слегка остудить";
  if (/^растоплен(?:ный|ная|ное|ного)$/.test(key)) return "растопленное";
  return note;
};

const EMOJI_RE = /[\p{Extended_Pictographic}\p{Emoji_Presentation}\uFE0F\u200D]/gu;
const SOCIAL_LINE_RE = /^(?:@[^\s]+|#[^\s]+(?:\s+#[^\s]+)*)$/i;
const PROMOTIONAL_RE = /\b(?:сохраняйте|сохраните\s+рецепт|подписывайтесь|подпишитесь|ставьте\s+лайк|поставьте\s+лайк|делитесь|отмечайте|пишите\s+в\s+комментариях)\b/i;
const LIFESTYLE_RE = /(?:совсем\s+не\s+про\s+хэллоуин|\bпро\s+уют\b|осенн(?:юю|яя|ей|ий|его)\s+щедрост|настоящий\s+вкус\s+осени|заварить\s+чай\s+и\s+съесть\s+первое\s+сразу|я\s+проверяла\s*[—–-]\s*этот\s+вариант\s+тоже\s+прекрасно\s+работает)/i;
const CULINARY_ACTION_RE = /(?:взб\w*|просе\w*|перемеш\w*|смеш\w*|добав\w*|вли\w*|выл\w*|разлож\w*|распредел\w*|выпек\w*|выпеч\w*|готов\w*|приготов\w*|соедин\w*|собер\w*|собир\w*|собра\w*|сверн\w*|украс\w*|посып\w*|нанес\w*|нанос\w*|отсад\w*|остуд\w*|охлад\w*|нагр\w*|подогр\w*|довест\w*|довод\w*|вар\w*|накр\w*|остав\w*|замоч\w*|обсуш\w*|растоп\w*|обжар\w*|измельч\w*|нарез\w*|размягч\w*)/i;
const CRITICAL_TECH_RE = /^(?:важно\s*:|[^.!?]*(?:должен|должна|должны)\s+(?:получиться|быть)|до\s+(?:сухой|готовности))/i;

const cleanTechnologyLine = (rawLine) => String(rawLine || "")
  .replace(EMOJI_RE, "")
  .replace(/\s+/g, " ")
  .replace(/^\s*[-–—•*·]+\s*/, "")
  .trim();

const comparableText = (value) => cleanTechnologyLine(value)
  .toLocaleLowerCase("ru")
  .replace(/[«»"'.,:;!?()[\]{}—–-]/g, "")
  .replace(/\s+/g, " ")
  .trim();

const normalizedTechnologyLines = (rawLines, title) => {
  const titleKey = comparableText(title);
  const seen = new Set();
  const result = [];

  for (const rawLine of rawLines) {
    const cleanLine = cleanTechnologyLine(rawLine);
    if (!cleanLine || SOCIAL_LINE_RE.test(cleanLine) || PROMOTIONAL_RE.test(cleanLine)
      || LIFESTYLE_RE.test(cleanLine)) continue;

    const key = headingKey(cleanLine);
    const heading = TECHNOLOGY_HEADINGS.get(key);
    const comparable = comparableText(cleanLine);
    if (!comparable || comparable === titleKey || seen.has(comparable)) continue;

    if (heading) {
      result.push(heading);
      seen.add(comparableText(heading));
      continue;
    }

    if (!CULINARY_ACTION_RE.test(cleanLine) && !CRITICAL_TECH_RE.test(cleanLine)) continue;
    result.push(cleanLine);
    seen.add(comparable);
  }
  return result;
};

const IMPORTANT_NOTE_ACTIONS = [
  { test: /растоп/i, evidence: /растоп/i },
  { test: /остуд|остыв/i, evidence: /остуд|остыв|охлад/i },
  { test: /обжар/i, evidence: /обжар/i },
  { test: /измельч|рублен/i, evidence: /измельч|рублен|нарез/i },
  { test: /просеян|просеять/i, evidence: /просе/i },
  { test: /размягч/i, evidence: /размягч|мягк/i },
];

const ingredientEvidenceRe = (name) => {
  const words = String(name || "").toLocaleLowerCase("ru").match(/[а-яёa-z]{4,}/g) || [];
  // Four-letter stems are conservative enough for ingredient terms while
  // matching Russian case endings ("орехи" / "орехами").
  const stems = words.map((word) => word.slice(0, Math.min(4, word.length)));
  return stems.length ? new RegExp(stems.map(escapeRegExp).join("|"), "i") : null;
};

const missingPreparationLine = (item, technologyLines) => {
  const note = item?.source_note;
  if (!note) return null;
  const relevant = IMPORTANT_NOTE_ACTIONS.filter(({ test }) => test.test(note));
  if (!relevant.length) return null;

  const ingredientRe = ingredientEvidenceRe(item.name);
  const hasEquivalent = technologyLines.some((line) => ingredientRe?.test(line)
    && relevant.every(({ evidence }) => evidence.test(line)));
  if (hasEquivalent) return null;

  if (/растоп/i.test(note) && /остуд|остыв/i.test(note)) {
    return `${item.name} растопить и слегка остудить.`;
  }
  if (/растоп/i.test(note)) return `${item.name} растопить.`;
  if (/обжар/i.test(note)) return `Использовать обжаренные ${item.name.toLocaleLowerCase("ru")}.`;
  if (/измельч|рублен/i.test(note)) return `Измельчить ${item.name.toLocaleLowerCase("ru")}.`;
  if (/просеян|просеять/i.test(note)) return `Просеять ${item.name.toLocaleLowerCase("ru")}.`;
  if (/размягч/i.test(note)) return `${item.name} размягчить.`;
  return null;
};

const matchLongestUnit = (rest) => {
  const lower = rest.toLowerCase();
  for (const [token, canonical] of UNIT_PREFIXES) {
    if (lower.startsWith(token)) {
      const next = rest[token.length];
      if (next === undefined || next === " " || next === "\t" || next === "." || /[,;:–—|\-]/.test(next)) {
        return { canonical, length: token.length + (next === "." ? 1 : 0) };
      }
    }
  }
  return null;
};

const tryAmountFirst = (line) => {
  const cleaned = stripListPrefix(line);
  const match = cleaned.match(new RegExp(`^${AMOUNT_SOURCE}\\s*(.+)$`));
  if (!match) return null;
  const amount = parseAmount(match[1]);
  const rest = match[2].trim();
  if (!rest) return null;
  const unitMatch = matchLongestUnit(rest);
  let unitRaw;
  let name;
  if (unitMatch) {
    unitRaw = unitMatch.canonical;
    name = rest.slice(unitMatch.length).replace(/^\s*[-–—|:]\s*/, "").trim();
  } else {
    const eggMatch = rest.match(/^(яйц(?:о|а)?|яиц)(?:\s+([СC]\d|S|M|L|XL))?(?=$|\s|[,.;:–—-])/i);
    if (!eggMatch) return null;
    unitRaw = "шт";
    name = `${eggMatch[1]}${eggMatch[2] ? ` ${eggMatch[2]}` : ""}`;
  }
  if (!name || !Number.isFinite(amount) || amount <= 0) return null;
  return { name: name.replace(/\s{2,}/g, " "), amount, unitRaw };
};

const tryNameFirst = (line) => {
  const cleaned = stripListPrefix(line);
  if (TECH_VERB_RE.test(cleaned)) return null;
  if (new RegExp(`^(?:${AMOUNT_TOKEN_SOURCE})(?=\\s|[A-Za-zА-Яа-яЁё])`).test(cleaned)) return null;
  for (const match of cleaned.matchAll(new RegExp(AMOUNT_TOKEN_SOURCE, "g"))) {
    const name = cleaned.slice(0, match.index).trim().replace(/[|:–—-]+$/, "").trim();
    const amount = parseAmount(match[0]);
    const rest = cleaned.slice(match.index + match[0].length).trim();
    const unitMatch = matchLongestUnit(rest);
    if (!name || !Number.isFinite(amount) || amount <= 0 || !unitMatch) continue;
    const note = rest.slice(unitMatch.length).replace(/^\s*[,;—–-]\s*/, "").trim();
    return { name: note ? `${name}, ${note}` : name, amount, unitRaw: unitMatch.canonical };
  }
  return null;
};

const WORD_QUANTITIES = [
  ["щепотка", "pinch"], ["щепотки", "pinch"], ["щепотку", "pinch"],
  ["горсть", "handful"], ["горсти", "handful"],
];
const tryWordQty = (line) => {
  const cleaned = stripListPrefix(line);
  const lower = cleaned.toLowerCase();
  for (const [token, unitRaw] of WORD_QUANTITIES) {
    if (lower.startsWith(token) && (cleaned[token.length] === undefined || cleaned[token.length] === " ")) {
      const name = cleaned.slice(token.length).trim();
      if (name) return { name: name.replace(/\s{2,}/g, " "), amount: 1, unitRaw };
    }
  }
  return null;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const UNIT_TOKEN_SOURCE = UNIT_PREFIXES.map(([token]) => escapeRegExp(token))
  .sort((a, b) => b.length - a.length)
  .join("|");
const QUANTITY_UNIT_RE = new RegExp(
  `(${AMOUNT_TOKEN_SOURCE})\\s*(${UNIT_TOKEN_SOURCE})(?=$|\\s|[.,;:–—-])`,
  "giu"
);
const EGG_QUANTITY_RE = new RegExp(
  `(${AMOUNT_TOKEN_SOURCE})\\s*(яйц(?:о|а)?|яиц)(?:\\s+([СC]\\d|S|M|L|XL))?(?=$|\\s|[,.;:–—-])`,
  "giu"
);
const TECH_WORD_RE = /(?:взб\w*|просе\w*|перемеш\w*|смеш\w*|добав\w*|вли\w*|выпек\w*|готов\w*|приготов\w*|соедин\w*|перелож\w*|собер\w*|собир\w*|сверн\w*|украс\w*|посып\w*|нанес\w*|распредел\w*|отсад\w*|остуд\w*|охлад\w*|замороз\w*|нагр\w*|дов[её]ст\w*|довод\w*|раскат\w*|накр\w*|остав\w*|процед\w*|измельч\w*|варить|варите|варим|варят)/i;

const cleanExtractedName = (segment) => {
  let value = String(segment || "")
    .replace(/\s+/g, " ")
    .replace(/^\s*(?:[-–—:;,]|и\b|с\b|со\b)+\s*/i, "")
    .trim();
  const verb = value.search(TECH_WORD_RE);
  if (verb >= 0) value = value.slice(0, verb);
  value = value.split(/[.;]/, 1)[0]
    .replace(/[-–—,;:\s]+$/, "")
    .replace(/\s+(?:и|с|со|в|во|для|к)\s*$/i, "")
    .trim();
  return value;
};

const extractQuantifiedIngredients = (line) => {
  const source = stripListPrefix(line);
  const matches = [];
  for (const match of source.matchAll(QUANTITY_UNIT_RE)) {
    const unitMatch = matchLongestUnit(match[2]);
    if (!unitMatch) continue;
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      amount: parseAmount(match[1]),
      unitRaw: unitMatch.canonical,
      fixedName: null,
    });
  }
  for (const match of source.matchAll(EGG_QUANTITY_RE)) {
    const overlaps = matches.some((entry) => match.index < entry.end && match.index + match[0].length > entry.start);
    if (overlaps) continue;
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      amount: parseAmount(match[1]),
      unitRaw: "шт",
      fixedName: `${match[2]}${match[3] ? ` ${match[3]}` : ""}`,
    });
  }
  matches.sort((a, b) => a.start - b.start);
  return matches.map((entry, index) => {
    if (entry.fixedName) return { ...entry, name: entry.fixedName };
    const nextStart = matches[index + 1]?.start ?? source.length;
    return { ...entry, name: cleanExtractedName(source.slice(entry.end, nextStart)) };
  }).filter((entry) => entry.name && Number.isFinite(entry.amount) && entry.amount > 0);
};

const isNonExactIngredientCandidate = (line) => /^(?:банка|банку|пачка|пачку|упаковка|упаковку)\s+/i
  .test(stripListPrefix(line));

const SAFE_RECIPE_UNITS = new Set(["g", "kg", "ml", "l", "pcs"]);
const REVIEW_WARNING = "Нужно уточнить единицу для расчётов";

export const ingredientReviewWarning = (item) =>
  item?.unit && !SAFE_RECIPE_UNITS.has(item.unit) ? REVIEW_WARNING : null;

const tryYield = (line) => {
  const cleaned = stripListPrefix(line);
  const serviceHeading = cleaned.match(new RegExp(
    `^ингредиенты\\s*(?::|[—–-])?\\s*на\\s*(${AMOUNT_TOKEN_SOURCE})\\s*(?:кекс(?:ов|а|ы)?|шт\\.?)\\s*$`,
    "i"
  ));
  if (serviceHeading) {
    const quantity = parseAmount(serviceHeading[1]);
    if (Number.isFinite(quantity) && quantity > 0) return { quantity, unit: "pcs" };
  }
  const match = cleaned.match(new RegExp(
    `^выход\\s*(?::|[—–-])?\\s*(${AMOUNT_TOKEN_SOURCE})\\s*(.+)$`,
    "i"
  ));
  if (!match) return null;
  const unitMatch = matchLongestUnit(match[2].trim());
  if (!unitMatch) return null;
  const key = unitMatch.canonical.toLowerCase().replace(/\\./g, "");
  const unit = UNIT_MAP[key] || key;
  const quantity = parseAmount(match[1]);
  if (!SAFE_RECIPE_UNITS.has(unit) || !Number.isFinite(quantity) || quantity <= 0) return null;
  return { quantity, unit };
};

export const parseRecipe = (raw) => {
  const originalText = String(raw ?? "");
  const lines = originalText.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const items = [];
  const technique = [];
  const unresolvedLines = [];
  const components = [];
  let title = "";
  let yieldQuantity = null;
  let yieldUnit = null;
  let currentSection = null;
  let methodStarted = false;
  let pendingMethodSubject = null;

  const ensureComponent = (name) => {
    let component = components.find((entry) => entry.name === name);
    if (!component) {
      component = { name, ingredients: [] };
      components.push(component);
    }
    return component;
  };

  const pushIngredient = (rawName, amount, unitRaw, rawLine) => {
    const key = (unitRaw || "").toLowerCase().replace(/\./g, "");
    const mappedUnit = UNIT_MAP[key] || key;
    if (!mappedUnit || TECH_VERB_RE.test(rawName) || TIME_TEMP_UNIT_RE.test(key) || !FOOD_UNIT_SET.has(mappedUnit)) {
      return null;
    }
    const split = splitIngredientIdentity(rawName);
    const name = normalizeIngredientName(split.name);
    if (name.length < 2 || !Number.isFinite(amount) || amount <= 0) return null;
    const sourceNote = normalizeIngredientNote(split.note, name);
    const item = {
      name,
      amount,
      unit: mappedUnit,
      // Keep source commentary out of the professional ingredient row. It is
      // retained transiently for safe technology transfer and verbatim in
      // original_text.
      note: null,
      source_note: sourceNote,
      section: currentSection,
      raw_line: rawLine,
    };
    items.push(item);
    if (currentSection) ensureComponent(currentSection).ingredients.push(item);
    return item;
  };

  const pushUnquantifiedDecorIngredient = (rawName, rawLine) => {
    const split = splitIngredientIdentity(rawName);
    const name = normalizeIngredientName(split.name);
    if (currentSection !== "Декор" || name.length < 2 || TECH_VERB_RE.test(name)) return null;
    const item = {
      name,
      amount: null,
      unit: null,
      note: null,
      source_note: normalizeIngredientNote(split.note, name),
      section: currentSection,
      raw_line: rawLine,
    };
    items.push(item);
    ensureComponent(currentSection).ingredients.push(item);
    return item;
  };

  const pushTechnique = (rawLine, extractedFacts = []) => {
    let cleanLine = stripListPrefix(rawLine);
    const sugarFact = extractedFacts.some((fact) => (
      normalizeIngredientName(splitIngredientIdentity(fact.name).name) === "Сахар"
    ));
    const instruction = cleanLine.match(/[—–-]\s*(.+)$/)?.[1]?.trim();

    // Some pasted recipes put the subject on the preceding ingredient line:
    // "4 яйца С1" then "170 г сахара — взбить миксером". Restore only that
    // explicit, adjacent context and keep the author's instruction unchanged.
    if (pendingMethodSubject && sugarFact && instruction && /взбить/i.test(instruction)
      && !/яйц/i.test(instruction)) {
      const normalizedInstruction = instruction.charAt(0).toLocaleLowerCase("ru") + instruction.slice(1);
      cleanLine = `${pendingMethodSubject} с сахаром ${normalizedInstruction}`;
      if (!/[.!?]$/.test(cleanLine)) cleanLine += ".";
    }

    if (cleanLine) technique.push(cleanLine);
    pendingMethodSubject = null;
  };

  for (const line of lines) {
    const recognizedYield = tryYield(line);
    if (recognizedYield) {
      yieldQuantity = recognizedYield.quantity;
      yieldUnit = recognizedYield.unit;
      continue;
    }

    const key = headingKey(line);
    if (GENERIC_HEADINGS.has(key)) {
      if (METHOD_HEADINGS.has(key)) {
        if (methodStarted && TECHNOLOGY_HEADINGS.has(key)) technique.push(TECHNOLOGY_HEADINGS.get(key));
        methodStarted = true;
      }
      continue;
    }
    if (SECTION_NAMES.has(key) && !methodStarted) {
      currentSection = SECTION_NAMES.get(key);
      ensureComponent(currentSection);
      continue;
    }
    if (methodStarted) {
      if (TECHNOLOGY_HEADINGS.has(key)) {
        technique.push(TECHNOLOGY_HEADINGS.get(key));
        continue;
      }
      const extractedItems = extractQuantifiedIngredients(line);
      for (const extracted of extractedItems) {
        pushIngredient(extracted.name, extracted.amount, extracted.unitRaw, line);
      }
      pushTechnique(line, extractedItems);
      if (extractedItems.length === 0 && isNonExactIngredientCandidate(line)) unresolvedLines.push(line);
      continue;
    }

    // Prefer the explicit ingredient grammar before the broad technology
    // verb scan. This keeps the complete modifier after commas/dashes so it
    // can be transferred or de-duplicated safely below.
    const earlyFacts = extractQuantifiedIngredients(line);
    const earlyAmountFirst = earlyFacts.length <= 1 ? tryAmountFirst(line) : null;
    if (earlyAmountFirst) {
      const adjacentSubject = pendingMethodSubject;
      const item = pushIngredient(earlyAmountFirst.name, earlyAmountFirst.amount, earlyAmountFirst.unitRaw, line);
      if (item) {
        const sourceInstruction = item.source_note || "";
        const handledAsPreparation = IMPORTANT_NOTE_ACTIONS.some(({ test }) => test.test(sourceInstruction));
        if (sourceInstruction && CULINARY_ACTION_RE.test(sourceInstruction) && !handledAsPreparation) {
          pendingMethodSubject = adjacentSubject;
          pushTechnique(line, [{ name: item.name }]);
        } else {
          pendingMethodSubject = /^Яйца(?:\s|$)/.test(item.name) ? item.name : null;
        }
        continue;
      }
    }

    const methodFacts = TECH_WORD_RE.test(line) ? extractQuantifiedIngredients(line) : [];
    if (methodFacts.length > 0) {
      for (const extracted of methodFacts) {
        pushIngredient(extracted.name, extracted.amount, extracted.unitRaw, line);
      }
      pushTechnique(line, methodFacts);
      continue;
    }

    const named = tryNameFirst(line);
    if (named) {
      const item = pushIngredient(named.name, named.amount, named.unitRaw, line);
      if (item) {
        pendingMethodSubject = /^Яйца(?:\s|$)/.test(item.name) ? item.name : null;
        continue;
      }
      unresolvedLines.push(line);
      continue;
    }
    const wordQty = tryWordQty(line);
    if (wordQty) {
      if (pushIngredient(wordQty.name, wordQty.amount, wordQty.unitRaw, line)) continue;
      unresolvedLines.push(line);
      continue;
    }

    const embedded = extractQuantifiedIngredients(line);
    if (embedded.length > 0) {
      for (const extracted of embedded) {
        pushIngredient(extracted.name, extracted.amount, extracted.unitRaw, line);
      }
      // "Взять 250 граммов сахара" is an ingredient instruction, not a
      // separate method step. The original sentence remains in original_text.
      if (!/^взять(?:\s|$)/i.test(stripListPrefix(line))) pushTechnique(line, embedded);
      continue;
    }

    // Decoration lists commonly omit a quantity (for example, "рубленые
    // орехи"). The section gives the line an unambiguous ingredient meaning,
    // but no mass may be invented: keep it editable and require manual weight.
    if (currentSection === "Декор" && /^[-–—•*·]/.test(line)) {
      if (pushUnquantifiedDecorIngredient(stripListPrefix(line), line)) continue;
    }

    const isPlainSentence = stripListPrefix(line) === line
      && /[.!?]$/.test(line)
      && line.split(/\s+/).length >= 4;
    if (TECH_WORD_RE.test(line) || isPlainSentence) {
      pushTechnique(line);
      continue;
    }

    const looksLikeHeading = !/^[-–—•*·]/.test(line) && line.length < 80;
    if (!title && !currentSection && looksLikeHeading && !line.includes(":")) {
      title = line;
      continue;
    }
    if (currentSection || /^[-–—•*·]/.test(line)) unresolvedLines.push(line);
    else pushTechnique(line);
  }

  const cleanTitle = cleanTechnologyLine(title);
  const cleanTechniqueLines = normalizedTechnologyLines(technique, cleanTitle);
  const preparedTechniqueLines = cleanTechniqueLines.map((line) => {
    const decorNuts = items.find((item) => item.section === "Декор"
      && item.name === "Грецкие орехи"
      && /рублен/i.test(item.source_note || "")
      && /обжар/i.test(item.source_note || ""));
    if (!decorNuts || /обжар/i.test(line) || !/рублеными\s+орехами/i.test(line)) return line;
    return line.replace(/рублеными\s+орехами/i, "рублеными обжаренными орехами");
  });
  const transferredPreparation = items
    .map((item) => missingPreparationLine(item, preparedTechniqueLines))
    .filter(Boolean);
  const finalTechniqueLines = [...new Set([...transferredPreparation, ...preparedTechniqueLines])];

  return {
    title: cleanTitle,
    yield_quantity: yieldQuantity,
    yield_unit: yieldUnit,
    items,
    components,
    unresolved_lines: unresolvedLines,
    technique: finalTechniqueLines.join("\n"),
    original_text: originalText,
  };
};

export const hasRecognizedIngredients = (result) => Array.isArray(result?.items)
  && result.items.some((item) => item?.name && Number(item?.amount) > 0 && item?.unit);
